import { ActionIcon, Loader, NavLink, Popover, Stack, Text, ThemeIcon, Tooltip } from '@mantine/core';
import {
    IconCheck,
    IconCircle,
    IconCircleOff,
    IconCircleXFilled,
    IconDownload,
    IconExclamationMark,
    IconX,
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OpenModal } from '../../../../../../react-commons/utils/modals/openModal';
import { elapsedTimeDisplayResolver } from '../../../../../../react-commons/utils/utilFunctions';
import { addFinishedBackgroundTask, setTaskRefresh } from '../../../../persisted/global/globalActions';
import {
    BackgroundExportTaskResult,
    BackgroundImportTaskResult,
    BackgroundNormalTaskResult,
    BackgroundTaskResource,
    BackgroundValidatedExportTaskResult,
} from '../../../../resource/services/backgroundTaskResource';
import { downloadFromBackgroundTask } from '../../../../resource/services/system/systemActions';
import { TOOLTIPS } from '../../../../resource/tooltipTexts';
import { RootState, useThunkDispatch } from '../../../../store/rootReducer';
import { showNotification } from '../../../util/showNotification';
import { BackgroundTaskInfoDialogContent } from './BackgroundTaskInfoDialogContent';
import './StatusComponent.scss';
import { useBackgroundTaskServiceActions } from '../../../../resource/services/backgroundTaskService';
import { useProCeedApplicationInformation } from '../../../../resource/services/useProCeedApplicationInformation';
import { useUserId } from '../../../authentication/useUserId';

export const StatusBarComponent = () => {
    const [downloadingTasks, setDownloadingTasks] = React.useState<Record<string, number | null>>({});

    const dispatch = useDispatch();
    const thunkDispatch = useThunkDispatch();
    const taskRefresh = useSelector((state: RootState) => state.persisted.global.taskRefresh);
    const finishedBackgroundTasks: string[] = useSelector(
        (state: RootState) => state.persisted.global.finishedBackgroundTasks ?? []
    );
    const uploadFileTasks = useSelector((state: RootState) => state.persisted.global.uploadingFileTasks ?? {});

    const queryClient = useQueryClient();
    const userId = useUserId('');
    const isAuthenticated = Boolean(userId);

    const backgroundTaskActions = useBackgroundTaskServiceActions();

    const maestraVersion = useProCeedApplicationInformation();
    const backgroundTasks = backgroundTaskActions.useGetAllBackgroundTasksOfUser(userId);
    const dismissTask = backgroundTaskActions.useDismissTask();
    const stopTask = backgroundTaskActions.useStopTask();

    const downloadAction = (taskId: string, taskName: string, extension: 'pvt' | 'html') => {
        const newDownloadingTasks = { ...downloadingTasks };
        newDownloadingTasks[taskId] = null;
        setDownloadingTasks(newDownloadingTasks);
        thunkDispatch(
            downloadFromBackgroundTask(taskName, taskId, extension, (progressEvent) => {
                const total = progressEvent.total;
                if (total) {
                    const progress = Math.round((progressEvent.loaded / total) * 100);
                    const newDownloadingTasks = { ...downloadingTasks };
                    newDownloadingTasks[taskId] = progress;
                    setDownloadingTasks(newDownloadingTasks);
                }
            })
        ).then((_) => {
            setTimeout(() => {
                const newRemovedDownloadingTasks: Record<string, number | null> = {};
                Object.keys(downloadingTasks).forEach((value) => {
                    if (value !== taskId) {
                        newRemovedDownloadingTasks[value] = downloadingTasks[value];
                    }
                });
                setDownloadingTasks(newRemovedDownloadingTasks);
            }, 1000);
        });
    };

    React.useEffect(() => {
        if (taskRefresh) {
            backgroundTasks.refetch().then();
            dispatch(setTaskRefresh(false));
        }
    }, [taskRefresh]);

    React.useEffect(() => {
        if (backgroundTasks.data) {
            let needsProjectInvalidation = false;
            backgroundTasks.data.forEach((task) => {
                if (task.isDone) {
                    if (finishedBackgroundTasks.find((value: string) => value === task.id) === undefined) {
                        if (task.category === 'PVT_IMPORT') {
                            if (task.result?.successful) {
                                needsProjectInvalidation = true;
                            }
                        } else if (task.category === 'PVT_EXPORT' && task.result?.downloaded === false) {
                            const name = task.properties.name;
                            showNotification({
                                title: 'PVT ready to Download',
                                severity: 'success',
                                downloadAction: () => downloadAction(task.id, name, 'pvt'),
                            });
                        } else if (task.category === 'PROCESS_AREA_EXPORT' && task.result?.downloaded === false) {
                            const name = task.properties.name;
                            showNotification({
                                title: 'Process Area Rich Text ready to Download',
                                severity: 'success',
                                downloadAction: () => downloadAction(task.id, name, 'html'),
                            });
                        }
                    }
                    dispatch(addFinishedBackgroundTask(task.id));
                }
            });
            if (needsProjectInvalidation) {
                void queryClient.invalidateQueries({ queryKey: ['PROJECTS_OUTLINE'] });
            }
        }
    }, [backgroundTasks.data]);

    const onOpenTaskInfo = (
        taskResult:
            | BackgroundNormalTaskResult
            | BackgroundImportTaskResult
            | BackgroundValidatedExportTaskResult
            | BackgroundExportTaskResult
            | null
    ) => {
        if (taskResult !== null) {
            OpenModal.ContentModal({
                title: 'Background Task Info',
                content: <BackgroundTaskInfoDialogContent backgroundTaskResult={taskResult} />,
            });
        }
    };

    const onStopTask = (taskId: string) => {
        OpenModal.AlertModal('Stopping Process', 'Are you sure you want to stop this Process?', () =>
            stopTask.mutate({
                id: taskId,
            })
        );
    };

    /* TODO const onStopUploadTask = (id: string, abortController: AbortController) => {
        modals.openConfirmModal({
            title: 'Stopping Process',
            children: (
                <Text size="sm">Are you sure you want to stop this Process?</Text>
            ),
            labels: { confirm: 'Confirm', cancel: 'Cancel' },
            onCancel: () => undefined,
            onConfirm: () => {
                abortController.abort();
                dispatch(removeFileUploadTask(id));
            },
        })
    };*/

    const popOverContent = (content: React.ReactNode) => {
        return (
            <Popover position="top-start" offset={1}>
                <Popover.Target>{content}</Popover.Target>
                <Popover.Dropdown style={{ padding: 0 }}>
                    <Stack style={{ backgroundColor: 'rgba(97, 97, 97, 0.92)', color: 'white' }} gap={0}>
                        {Object.keys(uploadFileTasks).map((value) => (
                            <NavLink
                                key={value}
                                miw={400}
                                className={'status-nav-link'}
                                rightSection={
                                    <Tooltip label={TOOLTIPS.STOP_PROCESS}>
                                        <ActionIcon
                                            size={'small'}
                                            color={'primaryTeal'}
                                            // onClick={() => onStopUploadTask(value, uploadFileTasks[value].abortController)}
                                        >
                                            <IconCircleOff />
                                        </ActionIcon>
                                    </Tooltip>
                                }
                                leftSection={<Loader size={20} color={'primary.4'} />}
                                label={
                                    uploadFileTasks[value].progress !== null ?
                                        `${uploadFileTasks[value].message}: ${uploadFileTasks[value].progress}%`
                                    :   `${uploadFileTasks[value].message} ...`
                                }
                            />
                        ))}
                        {backgroundTasks.data?.map((value) => (
                            <NavLink
                                key={value.id}
                                className={'status-nav-link'}
                                miw={400}
                                rightSection={getSecondaryAction(value)}
                                leftSection={getListIcon(value)}
                                label={
                                    value.isRunning ?
                                        `${value.name} [${value.phase}] : ${value.lastLogEntry?.message} (${elapsedTimeDisplayResolver(value.elapsedTime)})`
                                    : value.isDone && value.result?.successful && value.id in downloadingTasks ?
                                        downloadingTasks[value.id] !== null ?
                                            `${value.name}  (${elapsedTimeDisplayResolver(value.elapsedTime)}) - Downloading: ${downloadingTasks[value.id]}%`
                                        :   `${value.name}  (${elapsedTimeDisplayResolver(value.elapsedTime)}) - Downloading ...`

                                    : value.isDone && value.result?.successful ?
                                        `${value.name}  (${elapsedTimeDisplayResolver(value.elapsedTime)})`
                                    : value.isCancelled ?
                                        `${value.name} : Stopped (${elapsedTimeDisplayResolver(value.elapsedTime)})`
                                    : value.isDone ?
                                        `${value.name} : ${value.result?.failureReason}  (${elapsedTimeDisplayResolver(value.elapsedTime)})`
                                    :   `${value.name} : ${value.lastLogEntry?.message}  (${elapsedTimeDisplayResolver(value.elapsedTime)})`

                                }
                            />
                        ))}
                    </Stack>
                </Popover.Dropdown>
            </Popover>
        );
    };

    const getLeftSideSpanContent = () => {
        const uploadFileTaskAmount = Object.keys(uploadFileTasks).length;

        if (backgroundTasks.data && backgroundTasks.data.length + uploadFileTaskAmount > 1) {
            const inProgress = backgroundTasks.data.filter((value) => value.isRunning);
            const done = backgroundTasks.data.filter((value) => value.isDone);
            const successful = done.filter((value) => value.result?.successful === true);
            const failed = done.filter((value) => value.result?.successful === false);
            const text =
                inProgress.length + uploadFileTaskAmount > 0 && done.length > 0 ?
                    `${inProgress.length + uploadFileTaskAmount} tasks in progress and ${done.length} tasks done (${successful.length} successful, ${
                        failed.length
                    } failed)`
                : inProgress.length + uploadFileTaskAmount > 0 ?
                    `${inProgress.length + uploadFileTaskAmount} tasks in progress`
                : done.length > 0 ?
                    `${done.length} tasks done (${successful.length} successful, ${failed.length} failed)`
                :   '';
            return popOverContent(
                <div
                    style={{
                        display: 'inline-flex',
                        cursor: 'pointer',
                        alignItems: 'center',
                        height: 24,
                        paddingBottom: 3.5,
                    }}
                >
                    {inProgress.length + uploadFileTaskAmount === 0 ?
                        <ThemeIcon
                            variant={'transparent'}
                            size={20}
                            color={'white'}
                            style={{ margin: 0, marginTop: 0, marginRight: 5 }}
                        >
                            <IconCircle />
                        </ThemeIcon>
                    :   <Loader size={14} color={'white'} style={{ margin: 2, marginTop: 2, marginRight: 5 }} />}
                    <Text style={{ fontSize: 14, fontWeight: 450, color: 'white' }}>{text}</Text>
                </div>
            );
        } else if (backgroundTasks.data && backgroundTasks.data.length === 1) {
            const task = backgroundTasks.data[0];
            const text =
                task.isDone && task.result?.successful ? `Task ${task.name} is done (successful)`
                : task.isDone && task.isCancelled ? `Task ${task.name} is done (stopped)`
                : task.isDone && !task.result?.successful ? `Task ${task.name} is done (failed)`
                : `${task.name} [${task.phase}] : ${task.lastLogEntry?.message}`;
            return popOverContent(
                <div
                    style={{
                        display: 'inline-flex',
                        cursor: 'pointer',
                        alignItems: 'center',
                        height: 24,
                        paddingBottom: 3.5,
                    }}
                >
                    {task.isDone ?
                        <ThemeIcon
                            variant={'transparent'}
                            size={20}
                            color={'white'}
                            style={{ margin: 0, marginTop: 0, marginRight: 5 }}
                        >
                            <IconCircle />
                        </ThemeIcon>
                    :   <Loader size={14} color={'white'} style={{ margin: 2, marginTop: 2, marginRight: 5 }} />}
                    <Text style={{ fontSize: 14, fontWeight: 450, color: 'white' }}>{text}</Text>
                </div>
            );
        } else if (uploadFileTaskAmount === 1) {
            const task = uploadFileTasks[Object.keys(uploadFileTasks)[0]];
            return popOverContent(
                <div
                    style={{
                        display: 'inline-flex',
                        cursor: 'pointer',
                        alignItems: 'center',
                        height: 24,
                        paddingBottom: 3.5,
                    }}
                >
                    <Loader size={14} color={'white'} style={{ margin: 2, marginTop: 2, marginRight: 5 }} />
                    <Text
                        style={{
                            fontSize: 14,
                            fontWeight: 450,
                            color: 'white',
                        }}
                    >
                        {task.progress ? `${task.message}: ${task.progress}%` : `${task.message} ...`}
                    </Text>
                </div>
            );
        } else {
            return <div style={{ display: 'inline-flex' }} />;
        }
    };

    const getSecondaryAction = (task: BackgroundTaskResource) => {
        if (task.isDone) {
            if (
                (task.category === 'PVT_EXPORT' || task.category === 'PROCESS_AREA_EXPORT') &&
                !task.isCancelled &&
                task.result?.successful
            ) {
                const extension = task.category === 'PVT_EXPORT' ? 'pvt' : 'html';
                return (
                    <div style={{ display: 'inline-flex', minWidth: 30 }}>
                        <Tooltip label={TOOLTIPS.DOWNLOAD}>
                            <ActionIcon
                                variant={'transparent'}
                                size={'small'}
                                color={'primary.4'}
                                onClick={() => downloadAction(task.id, task.properties.name, extension)}
                            >
                                <IconDownload />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label={TOOLTIPS.DISMISS}>
                            <ActionIcon
                                variant={'transparent'}
                                size={'small'}
                                color={'primary.4'}
                                onClick={() => {
                                    dismissTask.mutate({
                                        id: task.id,
                                    });
                                }}
                            >
                                <IconCircleXFilled />
                            </ActionIcon>
                        </Tooltip>
                    </div>
                );
            } else if (task.result && !task.isCancelled) {
                return (
                    <div style={{ display: 'inline-flex', minWidth: 30 }}>
                        <Tooltip label={TOOLTIPS.TASK_INFO}>
                            <ActionIcon
                                variant={'transparent'}
                                size={'small'}
                                color={'primary.4'}
                                onClick={() => onOpenTaskInfo(task.result)}
                            >
                                <IconExclamationMark />
                            </ActionIcon>
                        </Tooltip>
                        <Tooltip label={TOOLTIPS.DISMISS}>
                            <ActionIcon
                                variant={'transparent'}
                                size={'small'}
                                color={'primary.4'}
                                onClick={() => {
                                    dismissTask.mutate({
                                        id: task.id,
                                    });
                                }}
                            >
                                <IconCircleXFilled />
                            </ActionIcon>
                        </Tooltip>
                    </div>
                );
            }
            return (
                <Tooltip label={TOOLTIPS.DISMISS}>
                    <ActionIcon
                        variant={'transparent'}
                        size={'small'}
                        color={'primary.4'}
                        onClick={() => {
                            dismissTask.mutate({
                                id: task.id,
                            });
                        }}
                    >
                        <IconCircleXFilled />
                    </ActionIcon>
                </Tooltip>
            );
        } else {
            return (
                <Tooltip label={TOOLTIPS.STOP_PROCESS}>
                    <ActionIcon
                        variant={'transparent'}
                        size={'small'}
                        color={'primary.4'}
                        onClick={() => onStopTask(task.id)}
                    >
                        <IconCircleOff />
                    </ActionIcon>
                </Tooltip>
            );
        }
    };

    const getListIcon = (task: BackgroundTaskResource) => {
        if (task.isDone && task.result?.successful) {
            return (
                <ThemeIcon variant={'transparent'} size={'sm'} color={'green.5'}>
                    <IconCheck />
                </ThemeIcon>
            );
        } else if (task.isDone) {
            if (task.isCancelled) {
                return (
                    <ThemeIcon variant={'transparent'} size={'sm'} color={'red.5'}>
                        <IconX />
                    </ThemeIcon>
                );
            }
            return (
                <Tooltip label={task.result?.failureMessage ?? TOOLTIPS.ERROR}>
                    <ThemeIcon variant={'transparent'} size={'sm'} color={'red.5'}>
                        <IconX />
                    </ThemeIcon>
                </Tooltip>
            );
        } else {
            return <Loader size={18} color={'primary.4'} />;
        }
    };

    return (
        <span className={'footer'}>
            {isAuthenticated && <span style={{ float: 'left', paddingLeft: 5 }}>{getLeftSideSpanContent()}</span>}
            <span style={{ float: 'right', paddingRight: 5 }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', height: 24, paddingBottom: 3.5 }}>
                    <Text style={{ fontSize: 14, color: 'white', fontWeight: 450 }}>
                        ProCeed {maestraVersion.data?.applicationVersion}
                    </Text>
                </div>
            </span>
        </span>
    );
};
